import moment from 'moment';

export const getDefaultFilterValues = () => ({
  startDate: '',
  endDate: '',
  statusFilter: [],
  typeFilter: [],
  vehicleFilter: '',
  guestFilter: '',
  phoneFilter: '',
});

const arraysEqual = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);

export const mapStatusFilterValues = (values, options) => {
  if (Array.isArray(values) && values.length > 1) {
    const groupOption = options.find(
      (option) => Array.isArray(option.value) && arraysEqual(option.value, values)
    );
    return [
      {
        value: values,
        label: groupOption ? groupOption.label : `${values.length} selected`,
      },
    ];
  }
  if (Array.isArray(values) && values.length === 1) {
    const match = options.find((option) => String(option.value) === String(values[0]));
    return match ? [match] : [{ value: values[0], label: String(values[0]) }];
  }
  return [];
};

export const mapTypeFilterValues = (values, options) => {
  if (Array.isArray(values) && values.length > 1) {
    return [
      {
        value: values,
        label: `${values.length} selected`,
      },
    ];
  }
  if (Array.isArray(values) && values.length === 1) {
    const match = options.find((option) => String(option.value) === String(values[0]));
    return match ? [match] : [{ value: values[0], label: String(values[0]) }];
  }
  return [];
};

export const buildFilterBarItems = (intl, statusOptions, typeOptions) => [
  {
    key: 'dateRange',
    type: 'dateRange',
    startKey: 'startDate',
    endKey: 'endDate',
    label: intl.formatMessage({ id: 'date' }),
    startPlaceholder: intl.formatMessage({ id: 'startDate', defaultMessage: 'Start Date' }),
    endPlaceholder: intl.formatMessage({ id: 'endDate', defaultMessage: 'End Date' }),
  },
  {
    key: 'statusFilter',
    type: 'select',
    label: intl.formatMessage({ id: 'status' }),
    placeholder: intl.formatMessage({ id: 'open', defaultMessage: 'Open' }),
    multi: true,
    options: statusOptions,
    valueMapper: (values) => mapStatusFilterValues(values, statusOptions),
  },
  {
    key: 'typeFilter',
    type: 'select',
    label: intl.formatMessage({ id: 'type' }),
    placeholder: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
    multi: true,
    options: typeOptions,
    valueMapper: (values) => mapTypeFilterValues(values, typeOptions),
  },
  {
    key: 'vehicleFilter',
    type: 'search',
    label: intl.formatMessage({ id: 'vehicle' }),
    placeholder: intl.formatMessage({
      id: 'vehiclePlaceholder',
      defaultMessage: 'ABC-1234',
    }),
  },
  {
    key: 'guestFilter',
    type: 'search',
    label: intl.formatMessage({ id: 'customerName' }),
    placeholder: intl.formatMessage({
      id: 'guestNamePlaceholder',
      defaultMessage: 'John Doe',
    }),
  },
  {
    key: 'phoneFilter',
    type: 'search',
    label: intl.formatMessage({ id: 'customerPhoneNumber' }),
    placeholder: intl.formatMessage({
      id: 'phoneNumberPlaceholder',
      defaultMessage: '555-555-1234',
    }),
  },
];

export const applyFilters = (reclamations, filterValues = {}) => {
  const {
    startDate,
    endDate,
    statusFilter = [],
    typeFilter = [],
    vehicleFilter = '',
    guestFilter = '',
    phoneFilter = '',
  } = filterValues;
  const normalizedVehicle = vehicleFilter.trim().toLowerCase();
  const normalizedGuest = guestFilter.trim().toLowerCase();
  const normalizedPhone = phoneFilter.replace(/\D/g, '');
  return reclamations.filter((reclamation) => {
    const issueDate = moment(reclamation.issueDate);
    if (startDate && issueDate.isBefore(moment(startDate), 'day')) {
      return false;
    }
    if (endDate && issueDate.isAfter(moment(endDate), 'day')) {
      return false;
    }

    if (Array.isArray(statusFilter) && statusFilter.length > 0) {
      const matchesStatus = statusFilter.some(
        (statusValue) => Number(statusValue) === Number(reclamation.status)
      );
      if (!matchesStatus) {
        return false;
      }
    }

    if (Array.isArray(typeFilter) && typeFilter.length > 0) {
      const matchesType = typeFilter.some(
        (typeValue) => Number(typeValue) === Number(reclamation.claimDamagePartId)
      );
      if (!matchesType) {
        return false;
      }
    }

    if (normalizedVehicle) {
      const text = (reclamation.vehicleDetails?.plate || '').toLowerCase();
      if (!text.includes(normalizedVehicle)) {
        return false;
      }
    }

    if (normalizedGuest) {
      const guestText = (reclamation.customerName || '').toLowerCase();
      if (!guestText.includes(normalizedGuest)) {
        return false;
      }
    }

    if (normalizedPhone) {
      const digits = (reclamation.customerPhone || '').replace(/\D/g, '');
      if (!digits.includes(normalizedPhone)) {
        return false;
      }
    }

    return true;
  });
};
